<template>
  <div class="ParticipationPanel d-flex">
    <aside class="ParticipationPanel__Aside flex-shrink-0 mr-3">
      <h3 class="ParticipationPanel__Title">
        {{ $t('participationPanel.title') }}
      </h3>
      <div class="w-75 mt-3">
        <p>
          {{ $t('participationPanel.description') }}
        </p>
      </div>
    </aside>
    <div class="ParticipationPanel__Main flex-grow-1 flex-shrink-1">
      <div class="d-flex align-items-center">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.participation.title') }}
        </span>

        <ToggleSwitch
          v-model="data.enabled"
          class="mb-0"
        />
        <span class="flex-grow-1 ml-3 text-muted" />
        <span id="participatieEnabled">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="participatieEnabled"
          triggers="hover"
        >
          {{ $t('participationPanel.options.participation.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.interaction.title') }}
        </span>

        <ToggleSwitch
          v-model="data.interactionEnabled"
          :disabled="!data.enabled"
          class="mb-0"
        />
        <span class="flex-grow-1 ml-3 text-muted" />
        <span id="interactionEnabled">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="interactionEnabled"
          triggers="hover"
        >
          <p class="mb-1">
            {{ $t('participationPanel.options.interaction.tooltip1') }}
            {{ participationLink }}.
          </p>

          {{ $t('participationPanel.options.interaction.tooltip2') }}
          {{ viewerLink }}.
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.interactionPastParticipations.title') }}
        </span>

        <ToggleSwitch
          v-model="data.interactionOnPastParticipations"
          :disabled="!data.interactionEnabled"
          class="mb-0"
        />
        <span class="flex-grow-1 ml-3 text-muted" />
        <span id="interactionOnPastParticipations">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="interactionOnPastParticipations"
          triggers="hover"
        >
          {{ $t('participationPanel.options.interactionPastParticipations.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.citizenSuggestions.title') }}
        </span>

        <ToggleSwitch
          v-model="data.suggestionsEnabled"
          :disabled="!data.interactionEnabled"
          class="mb-0"
        />
        <span class="flex-grow-1 ml-3 text-muted" />
        <span id="suggestionsEnabled">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="suggestionsEnabled"
          triggers="hover"
        >
          {{ $t('participationPanel.options.citizenSuggestions.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.legendToggleSTD.title') }}
        </span>

        <ToggleSwitch
          v-model="data.defaultLayerVisibility.suggestion"
          class="mb-0"
        />
        <span class="flex-grow-1 ml-3 text-muted" />
        <span id="defaultLayerVisibilitySuggestions">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="defaultLayerVisibilitySuggestions"
          triggers="hover"
        >
          {{ $t('participationPanel.options.legendToggleSTD.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.link') }}
        </span>
        <div class="flex-grow-1 mr-5">
          <b-input-group :prepend="`${participationUrl}/`">
            <b-form-input
              v-model.trim="data.slug"
              :state="data.enabled ? isUniqueSlug : null"
              :disabled="!data.enabled"
            />
          </b-input-group>
        </div>
      </div>

      <div class="ParticipationPanel__Divider" />

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.dateOpen.title') }}
        </span>
        <b-input
          v-model="data.openDate"
          :disabled="!data.enabled"
          class="flex-grow-1 mr-3"
          type="date"
        />
        <span id="openDate">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="openDate"
          triggers="hover"
        >
          {{ $t('participationPanel.options.dateOpen.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.dateClose.title') }}
        </span>
        <b-input
          v-model="data.closeDate"
          class="flex-grow-1 mr-3"
          type="date"
          :state="data.enabled ? isCloseDateAfterOpeningDate : null"
          :disabled="!data.enabled"
        />
        <span id="closeDate">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="closeDate"
          triggers="hover"
        >
          {{ $t('participationPanel.options.dateClose.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.preview.title') }}
        </span>

        <ToggleSwitch
          v-model="data.previewEnabled"
          :disabled="!data.enabled"
          class="mb-0"
        />
        <a
          v-if="data.previewEnabled"
          :href="`https://${participationUrl}/${data.slug}?preview=${data.previewCode}`"
          target="_blank"
          class="ml-3 text-muted"
          v-text="
            `${participationUrl}/${data.slug}?preview=${data.previewCode}`
          "
        />
        <span class="flex-grow-1 ml-3 text-sm text-muted" />
        <span id="previewEnabled">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="previewEnabled"
          triggers="hover"
        >
          {{ $t('participationPanel.options.preview.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.msgPostClose.title') }}
        </span>

        <ToggleSwitch
          v-model="data.showMessageAfterClose"
          :disabled="!data.enabled"
          class="mb-0"
        />
        <span class="flex-grow-1 ml-3 text-muted" />
        <span id="showMessageAfterClose">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="showMessageAfterClose"
          triggers="hover"
        >
          {{ $t('participationPanel.options.msgPostClose.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <div class="ParticipationPanel__Label mr-3 mt-5">
          <span> {{ $t('participationPanel.textClosingMessage.title') }} </span>
        </div>
        <QuillEditor
          :initial-content="data.closingMessage"
          text="closingMessage"
          @content-changed="handleEditorChange"
        />
        <span id="closingMessageText">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="closingMessageText"
          triggers="hover"
        >
          {{ $t('participationPanel.textClosingMessage.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.logoBG.title') }}
        </span>
        <b-input
          v-model="data.logoBackgroundColor"
          class="flex-grow-1 mr-3"
          type="color"
          :disabled="!data.enabled"
        />
        <span id="logoBackgroundColor">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="logoBackgroundColor"
          triggers="hover"
        >
          {{ $t('participationPanel.options.logoBG.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.showPolicy.title') }}
        </span>

        <ToggleSwitch
          v-model="data.showPolicy"
          :disabled="!data.enabled"
          class="mb-0"
        />
        <span class="flex-grow-1 ml-3 text-muted" />
        <span id="showPolicy">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="showPolicy"
          triggers="hover"
        >
          {{ $t('participationPanel.options.showPolicy.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <div class="ParticipationPanel__Label mr-3 mt-5">
          <span> {{ $t('participationPanel.defaultPolicy.title') }} </span>
        </div>
        <QuillEditor
          :initial-content="data.policyText"
          text="policyText"
          @content-changed="handleEditorChange"
        />
        <span id="policyText">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="policyText"
          triggers="hover"
        >
          {{ $t('participationPanel.defaultPolicy.tooltip') }}
        </b-tooltip>
      </div>

      <div class="ParticipationPanel__Divider" />

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.statusStations.title') }}

          <SelectStatusesModal
            :preselected="statuses"
            :preselected-comment-config="data.statusCommentConfig ?? []"
            @select="handleStatusSelect"
          />
        </span>

        <vSelect
          v-model="statuses"
          :disabled="true"
          class="flex-grow-1 mr-3"
          multiple
        />
        <span id="statuses">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="statuses"
          triggers="hover"
        >
          {{ $t('participationPanel.options.statusStations.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.phases.title') }}
        </span>
        <vSelect
          v-model="selectedPhases"
          :options="phaseOptions"
          :disabled="!data.enabled"
          :searchable="false"
          :select-group-label="$t('phases')"
          class="flex-grow-1 mr-3"
          multiple
        />
        <span id="fases">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="fases"
          triggers="hover"
        >
          {{ $t('participationPanel.options.phases.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.sources.title') }}
        </span>
        <vSelect
          v-model="selectedSources"
          :options="sourceOptions"
          :disabled="!data.enabled"
          :searchable="false"
          :select-group-label="$t('sources')"
          class="flex-grow-1 mr-3"
          multiple
        />
        <span id="sources">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="sources"
          triggers="hover"
        >
          {{ $t('participationPanel.options.sources.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.cpos.title') }}
        </span>
        <vSelect
          v-model="cpos"
          :options="cpoOptions"
          :disabled="!data.enabled"
          class="flex-grow-1 mr-3"
          multiple
        />
        <span id="cpos">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="cpos"
          triggers="hover"
        >
          {{ $t('participationPanel.options.cpos.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <div class="ParticipationPanel__Label mr-3 mt-5">
          <span>{{ $t('participationPanel.defaultIntro.title') }}</span>
        </div>
        <QuillEditor
          :initial-content="data.introductionText"
          text="introductionText"
          @content-changed="handleEditorChange"
        />
        <span id="introductionText">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>

        <b-tooltip
          target="introductionText"
          triggers="hover"
        >
          {{ $t('participationPanel.defaultIntro.tooltip') }}
        </b-tooltip>
      </div>

      <div v-if="!data.interactionEnabled">
        <div
          v-for="(cpo, cpoIndex) in data.cpos"
          :key="`cpo-${cpoIndex}`"
          class="d-flex align-items-center mt-4"
        >
          <div
            class="ParticipationPanel__Label mr-3 mt-4 d-flex flex-column align-items-start"
          >
            <div v-if="!!cpo.label">
              {{ $t('participationPanel.defaultSidebar.title') }}
              {{ cpo.label }}
            </div>
            <span v-else>{{
              $t('participationPanel.defaultSidebar.title') }}
            </span>
          </div>
          <QuillEditor
            :initial-content="cpo.sidebarText"
            :text="`cpo${cpoIndex}`"
            :index="cpoIndex"
            @content-changed="handleEditorChange"
          />
          <span :id="`sidebarText-${cpoIndex}`">
            <i class="far fa-fw fa-xl fa-info-circle" />
          </span>

          <b-tooltip
            :target="`sidebarText-${cpoIndex}`"
            triggers="hover"
          >
            {{ $t('participationPanel.defaultSidebar.tooltip') }}
          </b-tooltip>
        </div>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="ParticipationPanel__Label mr-3">
          {{ $t('participationPanel.options.geography.title') }}
        </span>
        <b-textarea
          v-model.trim="data.geography"
          :state="data.enabled ? geographyIsValid : null"
          :disabled="!data.enabled"
          :rows="data.geography ? 4 : 2"
          class="flex-grow-1 mr-3"
          @blur="beautifyGeography"
        />
        <span id="geography">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="geography"
          triggers="hover"
        >
          {{ $t('participationPanel.options.geography.tooltip') }}
        </b-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
//import Vue from 'vue'
import { mapGetters } from 'vuex'
import geojsonhint from 'geojsonhint'
import ToggleSwitch from '@/components/form/ToggleSwitch'
import vSelect from '@/components/form/vSelect'
import chargingpointCpoMixin from '@/mixins/chargingpoint/chargingpointCpoMixin'
import { statusesWithLabelDutch, statusesWithLabelEnglish } from '@/../shared/services/statusTranslations'
import SelectStatusesModal from '@/components/admin/municipalities/SelectStatusesModal.vue'
import chargingpointsLoadMixin from '@/mixins/chargingpoint/chargingpointsLoadMixin'
import QuillEditor from '@/components/common/QuillEditor'

export default {
  name: 'ParticipationPanel',
  components: { SelectStatusesModal, vSelect, ToggleSwitch, QuillEditor },
  mixins: [chargingpointCpoMixin, chargingpointsLoadMixin],
  props: {
    values: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      data: {},
      activePreviewSidebarText: -1,
      trackChanges: true,
      participationLink: process.env.VUE_APP_PARTICIPATION_URL,
      viewerLink: process.env.VUE_APP_VIEWER_URL,
      selectedSources: [],
    }
  },
  computed: {
    ...mapGetters('config', [
      'config',
      'getAllParticipationConfigs',
      'getPhases',
    ]),
    ...mapGetters('planmode', [
      'getLocationSources',
      'getChargingPoints',
    ]),
    participationUrl() {
      return this.isInteractionEnabled
        ? this.participationLink
        : this.viewerLink
    },
    isUniqueSlug() {
      if (!this.data.slug) {
        return false
      }

      const participationSlugs = this.getAllParticipationConfigs
        .filter(
          (participationConfig) =>
            participationConfig && participationConfig.uuid !== this.data.uuid,
        )
        .map(({ slug }) => slug)
        .filter((slug) => !!slug)

      return !participationSlugs.includes(this.data.slug)
    },
    isInteractionEnabled() {
      const [participation] = this.config.participation
      return participation.interactionEnabled
    },
    isCloseDateAfterOpeningDate() {
      if (!this.data.openDate || !this.data.closeDate) {
        return null
      }

      if (new Date(this.data.closeDate) >= new Date(this.data.openDate)) {
        return null
      }

      return false
    },
    activePhases() {
      const oldPhases = [
        { value: 1, label: 'Fase 1' },
        { value: 2, label: 'Fase 2' },
      ]
      const newActivePhases =
        this.config.phases
          ?.filter(({ active }) => !!active)
          .map(({ uuid, label }) => ({ value: uuid, label })) ?? []
      return [...oldPhases, ...newActivePhases]
    },
    phaseOptions() {
      // Only show the phases that are not yet selected
      return this.activePhases.filter(
        ({ value }) => !this.data.phases.includes(value),
      )
    },
    selectedPhases: {
      get() {
        return this.activePhases.filter(({ value }) =>
          this.data.phases.includes(value),
        )
      },
      set(newPhases) {
        this.data.phases = newPhases.map(({ value }) => value)
      },
    },

    sourceOptions() {
      return this.getLocationSources
        .filter(source => !this.selectedSources.some(s => s.value === source.type))
        .map(source => ({ value: source.type, label: source.label }))
    },

    cpos: {
      get() {
        return this.cpoOptions.filter(({ value }) =>
          this.data.cpos.some((cpo) => cpo.value === value),
        )
      },
      set(newCpos) {
        const [firstParticipationConfig] = this.values
        newCpos.map((newcpo) => {
          const existingCpo = this.data.cpos.find(
            (cpo) => cpo.value === newcpo.value,
          )
          if (existingCpo) {
            newcpo.sidebarText = existingCpo.sidebarText
            return newcpo
          } else {
            newcpo.sidebarText = firstParticipationConfig.sidebarText || ''
          }
        })

        const cpos = newCpos.length
          ? newCpos
          : [{ value: 'default', sidebarText: this.data.sidebarText }]
        this.data.cpos = cpos.sort((a, b) => a.label.localeCompare(b.label))
      },
    },
    cpoOptions() {
      return this.cpoSelectOptions.map(({ value, label }) => ({
        value,
        label,
      }))
    },
    statuses: {
      get() {
        return this.statusOptions.filter(({ value }) =>
          this.data.statuses.includes(value),
        )
      },
      set(newStatuses) {
        this.data.statuses = newStatuses.map(({ value }) => value)
      },
    },
    statusOptions() {
      const statusesLang = localStorage.getItem('language') === 'nl' ? statusesWithLabelDutch : statusesWithLabelEnglish

      return Object.entries(statusesLang).map(([value, label]) => ({ value, label }))
    },
    geographyIsValid() {
      if (!this.data.geography) {
        return null
      }

      return geojsonhint.hint(this.data.geography).length === 0
    },
  },
  watch: {
    /**
     * Values passed via props override the data, without signaling input changes
     */
    values: {
      immediate: true,
      handler() {
        this.fetchAllChargingPoints(this.config.code)
        this.setValues()
      },
    },
    /**
     * When any input changes, pass it on
     */
    data: {
      handler(data) {
        if (this.trackChanges) {
          const [, ...otherParticipationConfigs] = this.values
          this.$emit('input', [data, ...otherParticipationConfigs])
        }
      },
      deep: true,
    },
    selectedSources: {
      deep: true,
      handler (sources) {
        this.data.sources = sources
      },
    },
  },
  created () {
    this.selectedSources = this.data.sources
  },
  methods: {
    handleEditorChange({ text, content, index }) {
      if (index) {
        this.data.cpos[index].sidebarText = content
      } else {
        this.data[text] = content
      }
    },
    beautifyGeography() {
      if (!this.geographyIsValid) {
        return
      }

      this.data.geography = JSON.stringify(
        JSON.parse(this.data.geography),
        null,
        2,
      )
    },
    /**
     * Set prop values as new data, without signaling input changes
     */
    setValues() {
      this.trackChanges = false
      const [firstParticipationConfig] = this.values

      this.selectedSources = firstParticipationConfig.sources
      // Transform cpos from plain uuid's array to array of objects if needed, including sidebarText prop //
      let newCpos = []
      if (!firstParticipationConfig.cpos.length) {
        firstParticipationConfig.cpos.push({
          value: 'default',
          sidebarText: firstParticipationConfig.sidebarText,
        })
      } else if (firstParticipationConfig.cpos.some((cpo) => !cpo.value)) {
        // uuid's only (old array)
        firstParticipationConfig.cpos.map((cpoUuid) => {
          const cpo = this.cpoOptions.find(
            (operator) => operator.value === cpoUuid,
          )

          if (cpo) {
            newCpos.push({
              value: cpo.value,
              label: cpo.label,
              sidebarText: firstParticipationConfig.sidebarText || '',
            })
          }
        })
        firstParticipationConfig.cpos = newCpos
      }

      firstParticipationConfig.cpos.sort((a, b) =>
        a.label.localeCompare(b.label),
      )
      this.data = firstParticipationConfig

      this.$nextTick(() => {
        this.trackChanges = true
      })
    },
    handleStatusSelect({ selected, commentConfig }) {
      this.statuses = selected
      this.data.statusCommentConfig = commentConfig
    },
    async fetchAllChargingPoints(code) {
      try {
        await this.$_chargingpointsLoadMixin_loadChargingPoints({ code })
        // if (!this.data.sources || !this.data.sources.length) {
        //   Vue.set(this.data, 'sources', this.getLocationSources.map(s => ({ value: s.type, label: s.label })))
        // }
      } catch (error) {
        console.error('Error fetching charging points:', error)
      }
    },
  },
}
</script>

<style lang="scss">
.ParticipationPanel {
  &__Aside {
    width: 200px;

    @media (min-width: 1280px) {
      width: 275px;
    }
  }
  &__Main {
    font-size: 1.25rem;

    .form-control {
      width: 100px;
      font-size: 1.25rem;
    }
  }
  &__Label {
    flex-shrink: 0;
    width: 175px;
  }

  &__Divider {
    border-top: solid 1px lightgray;
    margin: 1.5rem 2rem 0 2rem;
  }
}
</style>
